import { Amplify } from 'aws-amplify';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { SnackbarKey, SnackbarProvider } from 'notistack';
import React, { useEffect, useRef, type JSX } from 'react';
import { Navigate, Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements, useNavigate } from 'react-router-dom';
import { theme } from './assets/theme';
import { WithAuthenticator } from './aws-amplify-react';
import { Layout } from './components/Layout/Layout';
import UserContextProvider from './contexts/userContext';
import VorgangContextProvider from './contexts/vorgangContext';
import { environment } from './env';
import { useUser } from './hooks/useUser';
import { LogoutPage } from './pages/LogoutPage';
import { RechnungPage } from './pages/Vorgang/RechnungPage';
import VorgangPage from './pages/Vorgang/VorgangPage';
import { ZahlungsimportSeite } from './pages/Zahlungsimport/ZahlungsimportSeite';
import UserVerwaltung from './screens/Administration/FranchiseUserVerwaltung';
import Mandanten from './screens/Administration/Mandanten';
import { MandantenkonfigurationPage } from './screens/Administration/Mandantenkonfiguration/MandantenkonfigurationPage';
import MandantenUserVerwaltung from './screens/Administration/MandantenUserVerwaltung';
import TextbausteineAdmin from './screens/Administration/TextbausteineAdmin';
import { Dashboard } from './screens/Dashboard/Dashboard';
import Profil from './screens/Profil';
import Personen from './screens/Stammdaten/Personen';
import Unternehmen from './screens/Stammdaten/Unternehmen';
import { VorgangAdministrationSeite } from './screens/Vorgang/administration/VorgangAdministrationSeite';
import Dokumente from './screens/Vorgang/Dokumente';
import Fahrzeug from './screens/Vorgang/Fahrzeug';
import Fotos from './screens/Vorgang/Fotos';
import Gutachten from './screens/Vorgang/Gutachten';
import Kunde from './screens/Vorgang/kunde/Kunde';
import Protokoll from './screens/Vorgang/Protokoll';
import Schadenbemerkungen from './screens/Vorgang/Schadenbemerkungen';
import UnfalldatenView from './screens/Vorgang/UnfalldatenView';
import Unterschriften from './screens/Vorgang/Unterschriften';
import Bewertung from './screens/Vorgang/Werte';

Amplify.configure(environment);

function ProtectedElement({ children }: { children: React.ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const { user, isAdmin, isExperts24Admin, isVerwaltung } = useUser();
  const isLoggedIn = !!user;

  useEffect(() => {
    if (!isLoggedIn || (!isAdmin && !isExperts24Admin && !isVerwaltung)) {
      navigate('/', { replace: true });
    }
  }, [navigate, isAdmin, isExperts24Admin, isVerwaltung, isLoggedIn]);

  return <>{children}</>;
}

function AuthenticatedLayout(): JSX.Element {
  return (
    <WithAuthenticator>
      <VorgangContextProvider>
        <Layout>
          <Outlet />
        </Layout>
      </VorgangContextProvider>
    </WithAuthenticator>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthenticatedLayout />}>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/vorgang/:id" element={<VorgangPage />}>
        <Route path="kunde" element={<Kunde />} />
        <Route path="unfalldaten" element={<UnfalldatenView />} />
        <Route path="fahrzeug" element={<Fahrzeug />} />
        <Route path="unterschriften" element={<Unterschriften />} />
        <Route path="fotos" element={<Fotos />} />
        <Route path="dokumente" element={<Dokumente />} />
        <Route path="rechnungen" element={<RechnungPage />} />
        <Route path="gutachten" element={<Gutachten />} />
        <Route path="protokoll" element={<Protokoll />} />
        <Route path="bewertung" element={<Bewertung />} />
        <Route path="schadenbemerkungen" element={<Schadenbemerkungen />} />
        <Route
          path="administration"
          element={
            <ProtectedElement>
              <VorgangAdministrationSeite />
            </ProtectedElement>
          }
        />
        <Route path="logout" element={<LogoutPage />} />
      </Route>

      <Route path="/admin/mandanten" element={<Mandanten />} />
      <Route path="/admin/userverwaltung" element={<UserVerwaltung />} />
      <Route path="/admin/mandantenkonfiguration" element={<MandantenkonfigurationPage />} />
      <Route path="/admin/textbausteine" element={<TextbausteineAdmin />} />
      <Route path="/admin/mandantenbenutzerverwaltung" element={<MandantenUserVerwaltung />} />

      <Route path="/profil/:username" element={<Profil />} />
      <Route path="/zahlungsimport" element={<ZahlungsimportSeite />} />

      <Route path="/stammdaten/person" element={<Personen />} />
      <Route path="/stammdaten/unternehmen" element={<Unternehmen />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>
  )
);

export function App(): JSX.Element {
  const notistackRef = useRef<SnackbarProvider>(null);

  const createClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <UserContextProvider>
          <SnackbarProvider
            maxSnack={5}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            action={(key) => (
              <IconButton onClick={createClickDismiss(key)} data-testid="benachrichtigungSchliessenDruecker" size="large">
                <CloseIcon color="secondary" fontSize="small" />
              </IconButton>
            )}
            ref={notistackRef}
          >
            <RouterProvider router={router} />
          </SnackbarProvider>
        </UserContextProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
